import commaNumber from 'comma-number';

import { getTimezoneDayjs } from '@/utils/time';
import { addProtocol } from '@/utils/util';

import { formatEngagement } from './format-engagement';

const FEEDBACK_GALLERY_ENGAGEMENT_LABEL_MAP = {
  subscribe_count: 'Subscribers',
  view_count: 'Views',
  comment_count: 'Comments',
  like_count: 'Likes',
  votes_up_count: 'Up votes',
  votes_down_count: 'Down votes',
  share_count: 'Shares',
  rate: 'Engagement rate',
} as const;

type FeedbackGalleryCardWithBlocks = FeedbackListItemT & {
  blocks?: FeedbackBlockT[];
};

export type FeedbackGalleryEngagementKey =
  keyof typeof FEEDBACK_GALLERY_ENGAGEMENT_LABEL_MAP;

export type FeedbackGalleryEngagementItem = {
  key: FeedbackGalleryEngagementKey;
  label: (typeof FEEDBACK_GALLERY_ENGAGEMENT_LABEL_MAP)[FeedbackGalleryEngagementKey];
  value: number;
};

const FEEDBACK_GALLERY_ENGAGEMENT_KEYS = Object.keys(
  FEEDBACK_GALLERY_ENGAGEMENT_LABEL_MAP,
) as FeedbackGalleryEngagementKey[];

const isPositiveFiniteNumber = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
};

export const resolveFeedbackGalleryTitle = (feedback: FeedbackListItemT): string => {
  return feedback.title || 'Untitled feedback';
};

export const resolveFeedbackGalleryProfileName = (
  feedback: FeedbackListItemT,
): string => {
  return (
    feedback.customer_info?.name ||
    feedback.customer ||
    feedback.source?.target ||
    'Unknown profile'
  );
};

export const resolveFeedbackGalleryProfileMeta = (
  feedback: FeedbackListItemT,
): string => {
  const profileName = resolveFeedbackGalleryProfileName(feedback);
  const dateDisplay = feedback.date
    ? getTimezoneDayjs(feedback.date).format('MMM DD, YYYY')
    : '';

  return dateDisplay ? `${dateDisplay}  ·  ${profileName}` : profileName;
};

export const resolveFeedbackGalleryOriginHref = (
  feedback: FeedbackListItemT,
): string | undefined => {
  const originLink = feedback.source?.link?.trim();
  return originLink ? addProtocol(originLink) : undefined;
};

export const resolveFeedbackGalleryVisibleBlocks = (
  feedback: FeedbackListItemT,
): FeedbackBlockT[] => {
  const blocks = (feedback as FeedbackGalleryCardWithBlocks).blocks;
  return (blocks ?? []).filter((block) => block?.snippet?.trim().length > 0);
};

export const resolveFeedbackGalleryEngagementItems = (
  feedback: FeedbackListItemT,
): FeedbackGalleryEngagementItem[] => {
  const engagement = feedback.engagement as
    | Partial<Record<FeedbackGalleryEngagementKey, unknown>>
    | undefined;

  return FEEDBACK_GALLERY_ENGAGEMENT_KEYS.reduce<FeedbackGalleryEngagementItem[]>(
    (items, key) => {
      const value = engagement?.[key];
      if (!isPositiveFiniteNumber(value)) {
        return items;
      }

      return [
        ...items,
        {
          key,
          label: FEEDBACK_GALLERY_ENGAGEMENT_LABEL_MAP[key],
          value,
        },
      ];
    },
    [],
  );
};

export const formatFeedbackGalleryEngagementValue = (
  itemKey: FeedbackGalleryEngagementKey,
  value: number,
): string => {
  if (itemKey === 'rate') {
    return `${commaNumber(value.toFixed(1))}%`;
  }
  return formatEngagement(value);
};

